import { useState } from "react";
import { useTranslation } from "@wix/fe-essentials-standalone";
import { useBiLogger } from "../../../bi/useBiLogger";
import { useTheme, SESSION_STORAGE_KEYS } from "@wix/picasso-common";
import { PAGE_AREA } from "../../../bi/constants";
import { navigateToProjects } from "../../../utils/utils";
import { useMobile } from "../../../hooks/useMobile";
import { useFigmaValidatorQuery } from "./FigmaValidator/useFigmaValidatorQuery";
import { useWebsiteValidatorQuery } from "./WebsiteValidator/useWebsiteValidatorQuery";

const BASE_URL = import.meta.env.BASE_URL;

export const useRecreateButtons = () => {
  const [isRecreateFromWebsiteOpen, setIsRecreateFromWebsiteOpen] =
    useState(false);
  const [isRecreateFromFigmaOpen, setIsRecreateFromFigmaOpen] = useState(false);
  const biLogger = useBiLogger();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isMobile = useMobile();
  const { validateFigmaFrame } = useFigmaValidatorQuery();
  const { validateWebsiteReachable } = useWebsiteValidatorQuery();

  const isDarkMode = theme === "dark";

  const handleWebsiteClick = () => {
    biLogger.trackUserClickPicasso({
      buttonName: "Website URL",
    });
    setIsRecreateFromWebsiteOpen(true);
  };

  const handleFigmaClick = () => {
    biLogger.trackUserClickPicasso({
      buttonName: "Figma link",
    });
    setIsRecreateFromFigmaOpen(true);
  };

  const validateWebsiteUrl = (url: string) => {
    const regex = /^https?:\/\/[^\s/$.?#].[^\s]*$/;
    return regex.test(url);
  };

  const validateFigmaUrl = (url: string) => {
    const regex = /https:\/\/www\.figma\.com\/design\/[a-zA-Z0-9_-]+/;
    return regex.test(url);
  };

  const imageSuffix = isDarkMode ? "dark" : "light";

  const figmaModalContent = {
    title: t("landing.recreateFromFigma.modal.title"),
    subtitle: t("landing.recreateFromFigma.modal.subtitle"),
    image: `${BASE_URL}images/recreate-figma-${imageSuffix}.svg`,
    inputLabel: t("landing.recreateFromFigma.input.label"),
    inputPlaceholder: t("landing.recreateFromFigma.input.placeholder"),
    primaryButtonText: t("landing.recreateFromFigma.button.generate"),
    secondaryButtonText: t("landing.recreateFromFigma.button.cancel"),
    errorMessage: t("landing.recreateFromFigma.error.invalidUrl"),
    ctaName: "Recreate from Figma Link modal - generate button click",
    helperText: t("landing.recreateFromFigma.input.helperText"),
  };

  const websiteModalContent = {
    title: t("landing.recreateFromWebsite.modal.title"),
    subtitle: t("landing.recreateFromWebsite.modal.subtitle"),
    image: `${BASE_URL}images/recreate-website-${imageSuffix}.svg`,
    inputLabel: t("landing.recreateFromWebsite.input.label"),
    inputPlaceholder: t("landing.recreateFromWebsite.input.placeholder"),
    primaryButtonText: t("landing.recreateFromWebsite.button.generate"),
    secondaryButtonText: t("landing.recreateFromWebsite.button.cancel"),
    errorMessage: t("landing.recreateFromWebsite.error.invalidUrl"),
    ctaName: "Recreate from Website URL modal - generate button click",
  };

  const handleGenerateWebsite = async (url: string): Promise<boolean> => {
    biLogger.trackUserClickPicasso({
      buttonName: "Generate",
      pageArea: PAGE_AREA.RECREATE_FROM_WEBSITE_MODAL,
    });

    const isValid = await validateWebsiteReachable(url);
    if (!isValid) {
      return false;
    }

    sessionStorage.setItem(SESSION_STORAGE_KEYS.WEBSITE_URL, url);
    navigateToProjects();
    return true;
  };

  const handleGenerateFigma = async (url: string): Promise<boolean> => {
    biLogger.trackUserClickPicasso({
      buttonName: "Generate",
      pageArea: PAGE_AREA.RECREATE_FROM_FIGMA_MODAL,
    });

    const isValid = await validateFigmaFrame(url);
    if (!isValid) {
      return false;
    }

    sessionStorage.setItem(SESSION_STORAGE_KEYS.FIGMA_URL, url);
    navigateToProjects();
    return true;
  };

  const handleWebsiteModalClose = () => {
    biLogger.trackUserClickPicasso({
      buttonName: "Cancel",
      pageArea: PAGE_AREA.RECREATE_FROM_WEBSITE_MODAL,
    });
    setIsRecreateFromWebsiteOpen(false);
  };

  const handleFigmaModalClose = () => {
    biLogger.trackUserClickPicasso({
      buttonName: "Cancel",
      pageArea: PAGE_AREA.RECREATE_FROM_FIGMA_MODAL,
    });
    setIsRecreateFromFigmaOpen(false);
  };

  return {
    isRecreateFromWebsiteOpen,
    isRecreateFromFigmaOpen,
    handleWebsiteModalClose,
    handleFigmaModalClose,
    handleWebsiteClick,
    handleFigmaClick,
    validateWebsiteUrl,
    validateFigmaUrl,
    figmaModalContent,
    websiteModalContent,
    handleGenerateWebsite,
    handleGenerateFigma,
    isMobile,
  };
};
